import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { partition } from 'lodash-es';

import SearchComponent from './SearchComponent';
import { LimitedFinanceMenuItems, LimitedFinanceMenuFooter } from './TypeaheadItems';
import { useMenuScroll } from './TypeaheadItems/hooks';

import { getGlobalSettings } from '../utils/global_settings';
import { restrictedFinanceAssets } from '../utils/helpers';

const HeaderSearch = ({
  availableAssets,
  userPresent,
  className = 'header-search',
  minLength = 0,
  placeholder = 'Search',
  disabled = false,
}) => {
  const [onMenuToggle, scrollState] = useMenuScroll();
  const { current: globalSettings } = useRef(getGlobalSettings());
  const { permissions } = globalSettings;

  const modifierKeyPrefix = navigator?.platform?.startsWith('Mac')
    ? '⌘' // command key
    : 'Ctrl'; // control key

  const searchAssets = useMemo(() => {
    if (permissions.allowFinanceLP) {
      const [companyAssets, restAssets] = partition(
        availableAssets,
        (asset) => asset === 'companies'
      );
      return [...companyAssets, ...restAssets];
    }

    return availableAssets;
  }, [availableAssets, permissions]);

  const menuMaxHeight = (() => {
    if (permissions.isLimitedFinanceAccess) return '570px';

    return '432px';
  })();

  const customMenuItems = useCallback((asset) => {
    if (permissions.isLimitedFinanceAccess && restrictedFinanceAssets.includes(asset)) {
      return ({ items, selectedAsset, renderItem }) => (
        <LimitedFinanceMenuItems
          items={items}
          selectedAsset={selectedAsset}
          renderItem={renderItem}
        />
      );
    }

    return undefined;
  }, []);

  const customMenuFooter = useCallback((asset) => {
    if (permissions.isLimitedFinanceAccess && restrictedFinanceAssets.includes(asset)) {
      return <LimitedFinanceMenuFooter asset={asset} />;
    }

    return undefined;
  }, []);

  return (
    <SearchComponent
      availableAssets={searchAssets}
      defaultSelectedAsset={searchAssets[0]}
      availableSearchBy={false}
      minLength={minLength}
      placeholder={
        disabled
          ? 'Search is not available'
          : `${placeholder.replace('(⌘+K)', `(${modifierKeyPrefix}+K)`)}`
      }
      userPresent={userPresent}
      showAdvancedSearchLink={userPresent}
      showAdvancedSearchLinkForRecent={userPresent}
      cancelRequestsAfterSelect
      isFocusOnKeyCombination
      disableRecentResults={false}
      disableSearchOnFocus={false}
      disableOnEnterDown={!userPresent}
      disabled={disabled}
      maxHeight={menuMaxHeight}
      className={cx(className, {
        'menu-scrolled-top': scrollState.top,
        'menu-scrolled-bottom': scrollState.bottom,
      })}
      customMenuItems={customMenuItems}
      renderCustomMenuFooter={customMenuFooter}
      onMenuToggle={onMenuToggle}
    />
  );
};

HeaderSearch.propTypes = {
  availableAssets: PropTypes.array.isRequired,
  userPresent: PropTypes.bool.isRequired,
  className: PropTypes.string,
  minLength: PropTypes.number,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default HeaderSearch;
